import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Avatar } from "@radix-ui/react-avatar";
import { AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { getFormattedDate } from "@/lib/date-manipulation";
import html2canvas from "html2canvas";
import QRCodeStyling from "qr-code-styling";
import { useToast } from "@/hooks/use-toast";

type Props = {
  data: any;
};

export default function MembershipCard({ data }: Props) {
  const [scale, setScale] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isBackside, setIsBackside] = useState(false);
  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scaleWrapperRef = useRef<HTMLDivElement>(null);
  const flipWrapperRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const infoContainerRef = useRef<HTMLDivElement>(null);
  const addressValueRef = useRef<HTMLDivElement>(null);
  const pukAddressValueRef = useRef<HTMLDivElement>(null);
  const memberNameRef = useRef<HTMLDivElement>(null);
  const companyNameRef = useRef<HTMLDivElement>(null);
  const companyAddressRef = useRef<HTMLDivElement>(null);
  const birthValueRef = useRef<HTMLDivElement>(null);
  const cardId = useRef(`card-${Math.random().toString(36).substr(2, 9)}`);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const BASE_ADDRESS_FONT = 11;
  const MIN_ADDRESS_FONT = 8;
  const BASE_BIRTH_FONT = 12;
  const MIN_BIRTH_FONT = 9;
  const BASE_MEMBER_NAME_FONT = 18;
  const MIN_MEMBER_NAME_FONT = 12;
  const BASE_COMPANY_NAME_FONT = 12;
  const MIN_COMPANY_NAME_FONT = 8;
  const BASE_COMPANY_ADDRESS_FONT = 10;
  const MIN_COMPANY_ADDRESS_FONT = 7;
  const [addressFontSize, setAddressFontSize] = useState(BASE_ADDRESS_FONT);
  const [pukAddressFontSize, setPukAddressFontSize] =
    useState(BASE_ADDRESS_FONT);
  const [birthFontSize, setBirthFontSize] = useState(BASE_BIRTH_FONT);
  const [memberNameFontSize, setMemberNameFontSize] =
    useState(BASE_MEMBER_NAME_FONT);
  const [companyNameFontSize, setCompanyNameFontSize] = useState(
    BASE_COMPANY_NAME_FONT
  );
  const [companyAddressFontSize, setCompanyAddressFontSize] = useState(
    BASE_COMPANY_ADDRESS_FONT
  );

  const CARD_WIDTH = 250;
  const CARD_HEIGHT = 430;

  const qrCode = useRef(
    new QRCodeStyling({
      width: 300,
      height: 300,
      image: "",
      margin: 4,
      qrOptions: {
        typeNumber: 0,
        mode: "Byte",
        errorCorrectionLevel: "M",
      },
      cornersSquareOptions: {
        type: "square",
        color: "#000000",
      },
      dotsOptions: {
        color: "#000000",
        type: "square",
      },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 2,
      },
      backgroundOptions: {
        color: "#ffffff",
      },
    })
  );

  const generateQRData = () => {
    const baseUrl = "https://fsplemspsi.id";
    const memberId =
      searchParams.get("id") || data?.membership_id || data?.card_id;
    return `${baseUrl}?id=${memberId}`;
  };

  useEffect(() => {
    if (qrRef.current) {
      qrCode.current.append(qrRef.current);
    }
  }, []);

  useEffect(() => {
    const memberId =
      searchParams.get("id") || data?.membership_id || data?.card_id;
    if (memberId) {
      qrCode.current.update({
        data: generateQRData(),
      });
    }
  }, [searchParams, data]);

  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.offsetWidth - 40;
      const containerHeight = window.innerHeight * 0.7;

      const scaleByWidth = containerWidth / CARD_WIDTH;
      const scaleByHeight = containerHeight / CARD_HEIGHT;

      const newScale = Math.min(scaleByWidth, scaleByHeight, 1.2);
      setScale(Math.max(newScale, 0.4));
    };

    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  useLayoutEffect(() => {
    const container = infoContainerRef.current;
    const addressElement = addressValueRef.current;
    const pukAddressElement = pukAddressValueRef.current;
    const birthElement = birthValueRef.current;

    if (!container || !addressElement || !birthElement) return;

    let currentBirthSize = BASE_BIRTH_FONT;
    let currentAddressSize = BASE_ADDRESS_FONT;
    let currentPukAddressSize = BASE_ADDRESS_FONT;

    const applyBirthStyle = () => {
      birthElement.style.fontSize = `${currentBirthSize}px`;
      birthElement.style.lineHeight = currentBirthSize <= 10 ? "1.2" : "1.25";
      birthElement.style.whiteSpace = "pre-line";
    };

    const applyAddressStyle = () => {
      addressElement.style.fontSize = `${currentAddressSize}px`;
      addressElement.style.lineHeight = currentAddressSize <= 10 ? "1.25" : "1.3";
    };

    const applyPukAddressStyle = () => {
      if (!pukAddressElement) return;
      pukAddressElement.style.fontSize = `${currentPukAddressSize}px`;
      pukAddressElement.style.lineHeight =
        currentPukAddressSize <= 10 ? "1.25" : "1.3";
    };

    applyBirthStyle();
    applyAddressStyle();
    if (pukAddressElement) {
      applyPukAddressStyle();
    }

    const fitsContainer = () =>
      container.scrollHeight <= container.clientHeight;

    while (!fitsContainer() && currentBirthSize > MIN_BIRTH_FONT) {
      currentBirthSize -= 1;
      applyBirthStyle();
    }

    while (!fitsContainer() && currentAddressSize > MIN_ADDRESS_FONT) {
      currentAddressSize -= 1;
      applyAddressStyle();
    }

    if (pukAddressElement) {
      while (!fitsContainer() && currentPukAddressSize > MIN_ADDRESS_FONT) {
        currentPukAddressSize -= 1;
        applyPukAddressStyle();
      }
    }

    if (currentBirthSize !== birthFontSize) {
      setBirthFontSize(currentBirthSize);
    }

    if (currentAddressSize !== addressFontSize) {
      setAddressFontSize(currentAddressSize);
    }

    if (pukAddressElement && currentPukAddressSize !== pukAddressFontSize) {
      setPukAddressFontSize(currentPukAddressSize);
    }
  }, [
    data?.address,
    data?.puk?.address,
    data?.puk_address,
    data?.birth_date,
    data?.birth_place,
    data?.name,
    isBackside,
  ]);

  useLayoutEffect(() => {
    const nameElement = memberNameRef.current;
    if (!nameElement || !data?.name) return;

    let currentSize = BASE_MEMBER_NAME_FONT;

    const applyStyle = () => {
      const lineHeightValue = currentSize <= 14 ? 1.1 : 1.15;
      nameElement.style.fontSize = `${currentSize}px`;
      nameElement.style.lineHeight = `${lineHeightValue}`;
      nameElement.style.whiteSpace = "nowrap";
      nameElement.style.display = "inline-block";
      nameElement.style.overflow = "visible";
      nameElement.style.textOverflow = "unset";
      const horizontalMargin = currentSize <= 16 ? 8 : 10;
      nameElement.style.marginLeft = `${horizontalMargin}px`;
      nameElement.style.marginRight = `${horizontalMargin}px`;
      nameElement.style.maxWidth = `calc(100% - ${horizontalMargin * 2}px)`;
    };

    applyStyle();

    const exceedsWidth = () =>
      nameElement.scrollWidth > nameElement.clientWidth + 0.5;

    while (exceedsWidth() && currentSize > MIN_MEMBER_NAME_FONT) {
      currentSize -= 1;
      applyStyle();
    }

    if (currentSize !== memberNameFontSize) {
      setMemberNameFontSize(currentSize);
    }
  }, [data?.name, memberNameFontSize]);

  const sanitizeCompanyValue = (value?: string | null) => {
    if (typeof value !== "string") return undefined;
    const cleaned = value.replace(/\r/g, "").trim();
    if (!cleaned || cleaned === "-") return undefined;
    return cleaned;
  };

  const rawPukName = data?.puk_name ?? data?.pukName ?? undefined;
  const rawPukAddress = data?.puk_address ?? data?.pukAddress ?? undefined;

  const companyName = sanitizeCompanyValue(rawPukName);
  const companyAddress = sanitizeCompanyValue(rawPukAddress);

  useLayoutEffect(() => {
    const nameElement = companyNameRef.current;
    const addressElement = companyAddressRef.current;

    const adjustElement = (
      element: HTMLDivElement,
      baseSize: number,
      minSize: number,
      maxLines: number,
      currentState: number,
      setState: React.Dispatch<React.SetStateAction<number>>
    ) => {
      if (typeof window === "undefined") return;

      let currentSize = baseSize;
      const applyStyle = () => {
        const lineHeightValue =
          maxLines === 1
            ? currentSize <= 9
              ? 1.1
              : 1.15
            : maxLines === 2
            ? currentSize <= 9
              ? 1.1
              : 1.15
            : maxLines === 3
            ? 1
            : currentSize <= 9
            ? 1.2
            : 1.25;
        element.style.fontSize = `${currentSize}px`;
        element.style.lineHeight = `${lineHeightValue}`;
        element.style.whiteSpace = maxLines === 1 ? "nowrap" : "pre-line";
        if (maxLines === 1) {
          element.style.overflow = "visible";
          element.style.textOverflow = "clip";
          element.style.display = "block";
        } else {
          element.style.overflow = "visible";
          element.style.textOverflow = "unset";
        }
      };

      applyStyle();

      const exceedsLines = () => {
        const computedLineHeight = parseFloat(
          window.getComputedStyle(element).lineHeight || "0"
        );
        const maxHeight = computedLineHeight * maxLines;
        const heightExceeded = element.scrollHeight > maxHeight + 0.5;

        if (maxLines === 1) {
          const widthExceeded =
            element.scrollWidth > element.clientWidth + 0.5;
          return heightExceeded || widthExceeded;
        }

        return heightExceeded;
      };

      while (exceedsLines() && currentSize > minSize) {
        currentSize -= 1;
        applyStyle();
      }

      if (currentSize !== currentState) {
        setState(currentSize);
      }
    };

    if (nameElement && companyName) {
      adjustElement(
        nameElement,
        BASE_COMPANY_NAME_FONT,
        MIN_COMPANY_NAME_FONT,
        1,
        companyNameFontSize,
        setCompanyNameFontSize
      );
    }

    if (addressElement && companyAddress) {
      adjustElement(
        addressElement,
        BASE_COMPANY_ADDRESS_FONT,
        MIN_COMPANY_ADDRESS_FONT,
        3,
        companyAddressFontSize,
        setCompanyAddressFontSize
      );
    }
  }, [
    companyName,
    companyAddress,
    companyNameFontSize,
    companyAddressFontSize,
  ]);

  const InfoRow = ({
    label,
    value,
    compact = false,
    valueRef,
    valueClassName,
    valueStyle,
  }: {
    label: string;
    value: React.ReactNode;
    compact?: boolean;
    valueRef?: React.RefObject<HTMLDivElement>;
    valueClassName?: string;
    valueStyle?: React.CSSProperties;
  }) => (
    <div className="flex flex-col gap-[2px]">
      <span className="text-[8px] uppercase tracking-[0.2em] text-gray-600">
        {label}
      </span>
      <div
        ref={valueRef}
        className={`font-semibold text-gray-900 break-words ${
          compact ? "text-[11px] leading-[1.25]" : "text-[12px] leading-[1.3]"
        } whitespace-pre-line ${valueClassName || ""}`}
        style={valueStyle}
      >
        {value || "-"}
      </div>
    </div>
  );

  const downloadCardImage = async (side: "front" | "back") => {
    const cardElement =
      side === "front" ? frontCardRef.current : backCardRef.current;
    if (!cardElement) return;

    const originalScaleTransform =
      scaleWrapperRef.current?.style.transform ?? "";
    const originalFlipTransform =
      flipWrapperRef.current?.style.transform ?? "";
    const originalFrontTransform =
      frontCardRef.current?.style.transform ?? "";
    const originalBackTransform = backCardRef.current?.style.transform ?? "";
    const originalFrontVisibility =
      frontCardRef.current?.style.visibility ?? "";
    const originalBackVisibility =
      backCardRef.current?.style.visibility ?? "";
    const originalFrontZIndex = frontCardRef.current?.style.zIndex ?? "";
    const originalBackZIndex = backCardRef.current?.style.zIndex ?? "";

    try {
      toast({
        title:
          side === "front"
            ? "Kartu depan sedang diproses"
            : "Kartu belakang sedang diproses",
        description: "Mohon tunggu sebentar...",
      });

      setIsPrinting(true);

      if (scaleWrapperRef.current) {
        scaleWrapperRef.current.style.transform = "none";
      }

      if (flipWrapperRef.current) {
        flipWrapperRef.current.style.transform = "rotateY(0deg)";
      }

      if (frontCardRef.current) {
        frontCardRef.current.style.transform = "rotateY(0deg)";
        frontCardRef.current.style.visibility =
          side === "front" ? "visible" : "hidden";
        frontCardRef.current.style.zIndex = side === "front" ? "2" : "0";
      }

      if (backCardRef.current) {
        backCardRef.current.style.transform = "rotateY(0deg)";
        backCardRef.current.style.visibility =
          side === "back" ? "visible" : "hidden";
        backCardRef.current.style.zIndex = side === "back" ? "2" : "0";
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(cardElement, {
        scale: 6,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        logging: false,
        imageTimeout: 30000,
        removeContainer: true,
        foreignObjectRendering: false,
        onclone: (clonedDoc: Document) => {
          const clonedElement = clonedDoc.querySelector(
            ".print-content"
          ) as HTMLElement;
          if (clonedElement) {
            clonedElement.style.transform = "none";
            clonedElement.style.webkitTransform = "none";
            clonedElement.style.textRendering = "optimizeLegibility";
          }

          const printStyles = clonedDoc.createElement("style");
          printStyles.textContent = `
            .print-hidden { display: none !important; }
            .print-content {
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
              image-rendering: pixelated;
              text-rendering: optimizeLegibility;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            .idcard-container canvas {
              image-rendering: -webkit-optimize-contrast !important;
              image-rendering: crisp-edges !important;
            }
            .idcard-badge {
              padding-top:0 !important;
              margin-top: -2px !important;
            }
          `;
          clonedDoc.head.appendChild(printStyles);
        },
      });

      const link = document.createElement("a");
      link.download = `member-card-${
        data?.membership_id || "unknown"
      }-${side === "front" ? "front" : "back"}-hd.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();

      toast({
        title:
          side === "front"
            ? "Kartu depan berhasil diunduh"
            : "Kartu belakang berhasil diunduh",
        description: "Kartu anggota HD telah tersimpan sebagai gambar PNG",
      });
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Gagal mengunduh kartu",
        description: "Terjadi kesalahan saat memproses kartu",
        variant: "destructive",
      });
    } finally {
      if (scaleWrapperRef.current) {
        scaleWrapperRef.current.style.transform = originalScaleTransform;
      }

      if (flipWrapperRef.current) {
        flipWrapperRef.current.style.transform = originalFlipTransform;
      }

      if (frontCardRef.current) {
        frontCardRef.current.style.transform = originalFrontTransform;
        frontCardRef.current.style.visibility = originalFrontVisibility;
        frontCardRef.current.style.zIndex = originalFrontZIndex;
      }

      if (backCardRef.current) {
        backCardRef.current.style.transform = originalBackTransform;
        backCardRef.current.style.visibility = originalBackVisibility;
        backCardRef.current.style.zIndex = originalBackZIndex;
      }

      setIsPrinting(false);
    }
  };

  const formattedBirthDate = (() => {
    if (!data?.birth_date) return "";
    try {
      return new Date(data.birth_date).toLocaleDateString("id-ID");
    } catch (error) {
      return "";
    }
  })();

  const birthInfo = [data?.birth_place, formattedBirthDate]
    .filter(Boolean)
    .join(", ");
  const addressLineHeight = addressFontSize <= 10 ? 1.25 : 1.3;
  const pukAddressLineHeight = pukAddressFontSize <= 10 ? 1.25 : 1.3;
  const birthLineHeight = birthFontSize <= 10 ? 1.2 : 1.25;

  const companyNameLineHeight = companyNameFontSize <= 9 ? 1.1 : 1.15;
  const companyAddressLineHeight = 1;

  const getPukName = () => companyName || "";

  const getPukAddress = () => companyAddress || "";

  const hasExpired = () => {
    if (!data?.role_name) return false;
    if (!data?.expired_at) return false;
    try {
      const expiredDate = new Date(data.expired_at);
      const now = new Date();
      expiredDate.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);
      return expiredDate < now;
    } catch (error) {
      return false;
    }
  };

  const getStatusBackground = () => {
    if (hasExpired()) {
      return "bg-red-500";
    }

    if (data?.status === "active") {
      const membershipId = data?.membership_id || "";
      if (membershipId.includes("DPP")) return "bg-green-500";
      if (membershipId.includes("DPD")) return "bg-cyan-400";
      if (membershipId.includes("DPC")) return "bg-yellow-500";
      if (membershipId.includes("PUK")) return "bg-blue-900";
      return "bg-blue-500";
    }

    if (data?.status === "inactive") return "bg-gray-500";
    if (data?.status === "pending") return "bg-gray-200";
    if (data?.status === "rejected") return "bg-red-500";

    return "bg-red-500";
  };

  const getStatusText = () => {
    if (hasExpired()) {
      return "EXPIRED";
    }

    switch (data?.status) {
      case "active":
        return "AKTIF";
      case "inactive":
        return "NONAKTIF";
      case "pending":
        return "PENDING";
      case "rejected":
        return "DITOLAK";
      default:
        return "EXPIRED";
    }
  };

  const getRoleDisplay = () => data?.role_name?.trim() || "";
  const roleDisplay = getRoleDisplay();

  return (
    <div className="w-full max-w-md mx-auto text-center">
      <div className="shadow-md bg-white/10 backdrop-blur-md border border-white/30 rounded-2xl p-2">
        <div className="bg-white bg-[url('/bg-pattern.jpg')] bg-cover bg-center border border-dashed border-white/30 rounded-xl backdrop-blur-sm p-5">
          <div
            ref={containerRef}
            className="w-full flex justify-center items-center"
            style={{
              height: `${CARD_HEIGHT * scale + 20}px`,
              minHeight: "300px",
            }}
          >
            <div
              ref={scaleWrapperRef}
              className="relative"
              style={{
                width: `${CARD_WIDTH}px`,
                height: `${CARD_HEIGHT}px`,
                transform: `scale(${scale})`,
                transformOrigin: "center center",
                perspective: "2000px",
              }}
            >
              <div
                ref={flipWrapperRef}
                className="relative w-full h-full transition-transform duration-500"
                style={{
                  transformStyle: "preserve-3d",
                  transform: isBackside ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                <Card
                  ref={frontCardRef}
                  id={cardId.current}
                  className="absolute inset-0 overflow-hidden shadow-2xl border-0 print-content"
                  style={{
                    backgroundImage: `url("/bg.png")`,
                    backgroundPosition: "center",
                    backgroundSize: "300%",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#E9EAEC",
                    borderColor: "#DEE0E3A3",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <div className="relative z-10 p-2 h-full flex flex-col justify-between">
                    <div className="text-center mb-2">
                      <img
                        src="/card-header.png"
                        className="w-[270px] h-[145px] mx-auto"
                        style={{
                          imageRendering: isPrinting ? "crisp-edges" : "auto",
                        }}
                      />
                    </div>

                    <div className="flex justify-center mb-2 -mt-3">
                      <Avatar className="h-[90px] w-[90px] rounded-full bg-black border-2 border-white shadow-lg">
                        <AvatarImage
                          src={data?.photo_profile}
                          alt={data?.name}
                          className="rounded-full object-cover"
                          style={{
                            imageRendering: isPrinting
                              ? "crisp-edges"
                              : "auto",
                          }}
                        />
                        <AvatarFallback className="text-sm bg-black text-white">
                          {data?.name
                            ? data.name
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")
                                .toUpperCase()
                                .slice(0, 2)
                            : "NA"}
                        </AvatarFallback>
                      </Avatar>
                    </div>

                    <div className="flex-1 flex flex-col justify-between">
                      <div className="text-center">
                        <div
                          ref={memberNameRef}
                          className="mb-1 uppercase text-gray-900 font-black"
                          style={{
                            fontSize: `${memberNameFontSize}px`,
                            lineHeight: memberNameFontSize <= 14 ? "1.1" : "1.15",
                            textRendering: isPrinting
                              ? "optimizeLegibility"
                              : "auto",
                          }}
                        >
                          {data?.name}
                        </div>
                        {roleDisplay ? (
                          <div
                            className="text-xs px-4 uppercase text-gray-700"
                            style={{
                              fontSize: "14px",
                              fontWeight: 700,
                              textRendering: isPrinting
                                ? "optimizeLegibility"
                                : "auto",
                            }}
                          >
                            {roleDisplay}
                          </div>
                        ) : null}
                        <div className="mb-[2px] text-gray-800">
                          <span className="text-sm" style={{ fontSize: "12px" }}>
                            ID :{" "}
                          </span>
                          <span className="font-mono" style={{ fontSize: "12px" }}>
                            {data?.membership_id}
                          </span>
                        </div>
                        {(companyName || companyAddress) && (
                          <div className="px-4 text-gray-800 flex flex-col gap-[1px] items-center text-center">
                            {companyName && (
                              <div
                                ref={companyNameRef}
                                className="font-semibold text-gray-900 w-full break-words"
                                style={{
                                  fontSize: `${companyNameFontSize}px`,
                                  lineHeight: companyNameLineHeight,
                                  maxWidth: "100%",
                                }}
                              >
                                {companyName}
                              </div>
                            )}
                            {companyAddress && (
                              <div
                                ref={companyAddressRef}
                                className="text-gray-700 whitespace-pre-line break-words w-full"
                                style={{
                                  fontSize: `${companyAddressFontSize}px`,
                                  lineHeight: companyAddressLineHeight,
                                  maxWidth: "100%",
                                }}
                              >
                                {companyAddress}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div
                        className={`px-2 py-2 rounded-lg flex items-start justify-between shadow-md ${getStatusBackground()}`}
                      >
                        <div className="flex flex-col">
                          <div
                            className="text-left px-1 pt-1 leading-tight text-xs uppercase idcard-badge text-white"
                            style={{ fontSize: "10px", lineHeight: "1" }}
                          >
                            {data?.role_name ? (
                              <div
                                style={{
                                  fontSize: "9px",
                                  fontWeight: "400",
                                  lineHeight: "1.2",
                                }}
                              >
                                Kepengurusan
                              </div>
                            ) : (
                              <div
                                style={{
                                  fontSize: "9px",
                                  fontWeight: "400",
                                  lineHeight: "1.2",
                                }}
                              >
                                Keanggotaan
                              </div>
                            )}

                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "700",
                                marginBottom: "2px",
                                lineHeight: "1",
                              }}
                            >
                              {getStatusText()}
                            </div>
                          </div>
                          <div
                            className="text-xs px-1 uppercase font-semibold text-white"
                            style={{ fontSize: "10px" }}
                          >
                            {data?.role_name ? (
                              <>
                                Berlaku s.d : &nbsp;
                                {getFormattedDate(
                                  data?.expired_at,
                                  "MMM y",
                                  true
                                )}
                              </>
                            ) : (
                              <>
                                Bergabung : &nbsp;
                                {getFormattedDate(
                                  data?.joined_at || data?.created_at,
                                  "MMM y",
                                  true
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="w-[45px] h-[45px] border-1 border-solid flex-shrink-0 overflow-hidden bg-white border-gray-300">
                          <div
                            ref={qrRef}
                            className="w-full h-full idcard-container"
                            style={{
                              imageRendering: isPrinting
                                ? "crisp-edges"
                                : "auto",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card
                  ref={backCardRef}
                  className="absolute inset-0 overflow-hidden shadow-2xl border-0 print-content"
                  style={{
                    backgroundImage: `url("/bg.png")`,
                    backgroundPosition: "center",
                    backgroundSize: "300%",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#E9EAEC",
                    borderColor: "#DEE0E3A3",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <div className="relative z-10 p-3 h-full flex flex-col gap-3">
                    <div className="text-center">
                      <img
                        src="/card-header.png"
                        className="w-[270px] h-[145px] mx-auto"
                        style={{
                          imageRendering: isPrinting ? "crisp-edges" : "auto",
                        }}
                      />
                    </div>
                    <div
                      ref={infoContainerRef}
                      className="flex-1 bg-white rounded-lg border border-white shadow px-3 py-4 text-left flex flex-col gap-2 overflow-hidden"
                      style={{ maxHeight: `${CARD_HEIGHT * 0.5}px` }}
                    >
                      <InfoRow label="Nama" value={data?.name || "-"} />
                      <InfoRow
                        label="Tempat / Tanggal Lahir"
                        value={birthInfo || "-"}
                        valueRef={birthValueRef}
                        valueStyle={{
                          fontSize: `${birthFontSize}px`,
                          lineHeight: birthLineHeight,
                          whiteSpace: "pre-line",
                        }}
                      />
                      <InfoRow
                        label="Agama"
                        value={
                          data?.religion ? data.religion.toUpperCase() : "-"
                        }
                      />
                      <InfoRow
                        label="Alamat"
                        value={data?.address || "-"}
                        compact
                        valueRef={addressValueRef}
                        valueStyle={{
                          fontSize: `${addressFontSize}px`,
                          lineHeight: addressLineHeight,
                        }}
                      />
                    </div>
                    <div className="text-[9px] text-gray-600 leading-snug text-center mt-auto mb-2">
                      <div>
                        Nomor Anggota:{" "}
                        <span className="font-semibold text-gray-900">
                          {data?.membership_id || "-"}
                        </span>
                      </div>
                      <div>Di generate oleh FSP LEM SPSI Online</div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          onClick={() => downloadCardImage("front")}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
          disabled={isPrinting}
        >
          <Download className="w-4 h-4" />
          {isPrinting ? "Memproses..." : "Download Depan"}
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsBackside((prev) => !prev)}
          disabled={isPrinting}
          className="flex-1 min-w-[120px]"
        >
          {isBackside ? "Tampilkan Depan" : "Lihat Backside"}
        </Button>
        <Button
          onClick={() => downloadCardImage("back")}
          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white gap-2"
          disabled={isPrinting}
        >
          <Download className="w-4 h-4" />
          {isPrinting ? "Memproses..." : "Download Backside"}
        </Button>
      </div>
    </div>
  );
}
